/**
 * // useWindowDimension.ts
 * * This hook returns the viewport/window height and width
 */

import { useEffect, useRef, useState } from 'react'

type CustomWindowDimensions = {
  width: number | undefined
  value: any
}

type Size = {
  width: number
  value: any
}

interface ICustomWindowDimensions {
  sizes: Size[]
  defaultSize: Size
}

export const useCustomWindowDimensions = ({ sizes, defaultSize }: ICustomWindowDimensions): CustomWindowDimensions => {
  const sizesRef = useRef(sizes)
  const defaultSizeRef = useRef(defaultSize)

  useEffect(() => {
    sizesRef.current = sizes
    defaultSizeRef.current = defaultSize
  }, [sizes, defaultSize])

  const getSize = (): Size => {
    if (typeof window === 'undefined') {
      return defaultSizeRef.current
    }

    for (let i = 0; i < sizesRef.current.length; i++) {
      const s = sizesRef.current[i]

      if (window.innerWidth >= s.width) {
        return s
      }
    }

    return defaultSizeRef.current
  }

  const [size, setSize] = useState<Size>(() => getSize())

  useEffect(() => {
    function handleResize(): void {
      const nextSize = getSize()
      setSize((prev) => (prev.width === nextSize.width && prev.value === nextSize.value ? prev : nextSize))
    }
    window.addEventListener('resize', handleResize)

    return (): void => window.removeEventListener('resize', handleResize)
  }, [])

  return { ...size }
}
